/**
 * Dynamic Input engine (2D-style cursor-following entry).
 *
 * Pure runtime helpers shared by the DOM capture layer (key handling +
 * cursor tooltip) and the in-canvas bridge (pointer projection + base-point
 * tracking). Holds no React state: cursor/anchor geometry lives in module
 * refs that the bridge updates on every canvas pointer move; the zustand
 * store only carries the UI text/feedback.
 */

import * as THREE from 'three';
import { GROUND_PLANE } from '../scene';
import { dispatchCommand } from '../CommandDispatcher';
import { runCanonicalCommand } from '../commands/commandActions';
import { useCommandInteract } from '../commands/commandInteract';
import { useAppStore } from '../../state/store';
import { useDynamicInputStore } from '../../state/useDynamicInputStore';
import { isoAxes, isoConstrainWithAxes } from '../drafting/isoPlane';

// ============================================================================
// ENGINE REFS (bound by the in-canvas bridge, read by DOM-side helpers)
// ============================================================================

export interface DynamicEngineRefs {
    /** Active camera of the workbench viewport. */
    camera: THREE.Camera | null;
    /** Canvas element (pointer-event synthesis target). */
    canvas: HTMLCanvasElement | null;
    /** Demand-render invalidator. */
    invalidate: (() => void) | null;
}

export const engineRefs: DynamicEngineRefs = {
    camera: null,
    canvas: null,
    invalidate: null,
};

export function bindDynamicEngineRefs(refs: Partial<DynamicEngineRefs>): void {
    Object.assign(engineRefs, refs);
}

// ============================================================================
// CURSOR-GROUND + ANCHOR TRACKING
// ============================================================================

/** Last committed point (real click or injected dynamic point) — the
 *  relative-input base point, mirroring 2D's input-from-last-point. */
let anchorPoint: THREE.Vector3 | null = null;
/** Live world-space intersection of the cursor ray with the ground plane. */
let cursorGround: THREE.Vector3 | null = null;

const _ray = new THREE.Raycaster();

export const getAnchorPoint = (): THREE.Vector3 | null => anchorPoint;
export const getCursorGround = (): THREE.Vector3 | null => cursorGround;

/** Publish a new relative-input base point (used by interact feeds that
 *  bypass the synthetic-click injection path). */
export function setAnchorPoint(point: THREE.Vector3 | null): void {
    anchorPoint = point ? point.clone() : null;
}

/** Canvas-local CSS pixel offset of a client point (R3F compute convention). */
function clientToOffset(clientX: number, clientY: number): { ox: number; oy: number } | null {
    if (!engineRefs.canvas) return null;
    const rect = engineRefs.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return { ox: clientX - rect.left, oy: clientY - rect.top };
}

function intersectGroundAtOffset(ox: number, oy: number): THREE.Vector3 | null {
    const { camera, canvas } = engineRefs;
    if (!camera || !canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
        (ox / rect.width) * 2 - 1,
        -((oy / rect.height) * 2 - 1)
    );
    _ray.setFromCamera(ndc, camera);
    return _ray.ray.intersectPlane(GROUND_PLANE, new THREE.Vector3());
}

/**
 * Recompute the cursor's ground-plane position from a native pointer event
 * (client coords converted through the canvas rect, matching R3F's own
 * offset-based NDC computation). Called by the bridge on every canvas
 * pointermove; publishes the cursor-following coordinate readout.
 */
export function updateCursorGroundFromClient(clientX: number, clientY: number): void {
    const off = clientToOffset(clientX, clientY);
    cursorGround = off ? intersectGroundAtOffset(off.ox, off.oy) : null;
    publishReadout();
}

/** Track real left-clicks as the new base point (canvas capture listener). */
export function onCanvasClick(clientX: number, clientY: number): void {
    const off = clientToOffset(clientX, clientY);
    const ground = off ? intersectGroundAtOffset(off.ox, off.oy) : null;
    if (ground) anchorPoint = ground;
}

const fmtXY = (p: THREE.Vector3) => `${p.x.toFixed(2)}, ${p.y.toFixed(2)}`;

function publishReadout(): void {
    const store = useDynamicInputStore.getState();
    if (!cursorGround) {
        store.setReadout(null);
        return;
    }
    let line = fmtXY(cursorGround);
    if (anchorPoint) {
        const dist = cursorGround.distanceTo(anchorPoint);
        const angle = (Math.atan2(cursorGround.y - anchorPoint.y, cursorGround.x - anchorPoint.x) * 180) / Math.PI;
        line += `   ${dist.toFixed(2)} < ${angle.toFixed(0)}`;
    }
    store.setReadout(line);
}

// ============================================================================
// VALUE PARSING  (X,Y | @DX,DY | D<A | D)
// ============================================================================

export type ParsedDynamicValue =
    | { kind: 'xy'; x: number; y: number }
    | { kind: 'relxy'; dx: number; dy: number }
    | { kind: 'polar'; d: number; angleDeg: number }
    | { kind: 'distance'; d: number };

const RE_ABS_XY = /^(-?\d*\.?\d+),(-?\d*\.?\d+)$/;
const RE_REL_XY = /^@(-?\d*\.?\d+),(-?\d*\.?\d+)$/;
const RE_POLAR = /^@?(\d*\.?\d+)<(-?\d*\.?\d+)$/;
const RE_DIST = /^(\d*\.?\d+)$/;

/** Parse a typed coordinate/distance string (2D point-entry grammar). */
export function parseDynamicValue(raw: string): ParsedDynamicValue | null {
    const text = raw.trim();
    let m = text.match(RE_ABS_XY);
    if (m) return { kind: 'xy', x: parseFloat(m[1]), y: parseFloat(m[2]) };
    m = text.match(RE_REL_XY);
    if (m) return { kind: 'relxy', dx: parseFloat(m[1]), dy: parseFloat(m[2]) };
    m = text.match(RE_POLAR);
    if (m) return { kind: 'polar', d: parseFloat(m[1]), angleDeg: parseFloat(m[2]) };
    m = text.match(RE_DIST);
    if (m) return { kind: 'distance', d: parseFloat(m[1]) };
    return null;
}

export interface ResolvedDynamicPoint {
    point: THREE.Vector3;
    /** Human label shown in the cursor tooltip, e.g. "5.00 < 45". */
    label: string;
}

export type DynamicPointError = { error: string };

/**
 * Resolve a parsed value into a world point. Relative/polar/distance forms
 * require a base point (last committed click or injected point); direct
 * distance entry additionally uses the live cursor direction, exactly like
 * 2D's rubber-band distance input.
 */
export function resolveDynamicPoint(raw: string): ResolvedDynamicPoint | DynamicPointError | null {
    const parsed = parseDynamicValue(raw);
    if (!parsed) return null;

    if (parsed.kind === 'xy') {
        const point = new THREE.Vector3(parsed.x, parsed.y, 0);
        return { point, label: fmtXY(point) };
    }

    if (!anchorPoint) return { error: 'No base point — click a start point first' };

    if (parsed.kind === 'relxy') {
        const point = new THREE.Vector3(anchorPoint.x + parsed.dx, anchorPoint.y + parsed.dy, 0);
        return { point, label: fmtXY(point) };
    }

    if (parsed.kind === 'polar') {
        // Iso draft branch (Faz 7.1): the typed angle snaps to the nearest
        // axis of the active isometric plane (OCS drafting_constrain math:
        // dominant |dot| axis projection of the would-be delta).
        const axes = isoAxes();
        if (axes && anchorPoint) {
            const rad = (parsed.angleDeg * Math.PI) / 180;
            const raw = new THREE.Vector3(
                anchorPoint.x + Math.cos(rad) * parsed.d,
                anchorPoint.y + Math.sin(rad) * parsed.d,
                0
            );
            const point = isoConstrainWithAxes(anchorPoint, raw, axes);
            const dist = point.distanceTo(anchorPoint);
            const ang = (Math.atan2(point.y - anchorPoint.y, point.x - anchorPoint.x) * 180) / Math.PI;
            return { point, label: `${dist.toFixed(2)} < ${ang.toFixed(0)}` };
        }
        const rad = (parsed.angleDeg * Math.PI) / 180;
        const point = new THREE.Vector3(
            anchorPoint.x + Math.cos(rad) * parsed.d,
            anchorPoint.y + Math.sin(rad) * parsed.d,
            0
        );
        return { point, label: `${parsed.d.toFixed(2)} < ${parsed.angleDeg.toFixed(0)}` };
    }

    // Direct distance entry: d along the current cursor direction from anchor
    if (!cursorGround) return { error: 'Move the cursor to set the direction' };
    const dir = cursorGround.clone().sub(anchorPoint);
    dir.z = 0;
    if (dir.length() < 1e-6) return { error: 'Move the cursor to set the direction' };
    dir.normalize();
    const point = anchorPoint.clone().addScaledVector(dir, parsed.d);
    // Iso draft branch (Faz 7.1): direct-distance entry rides the dominant
    // isometric axis while isodraft is on.
    const axes = isoAxes();
    const constrained = axes ? isoConstrainWithAxes(anchorPoint, point, axes) : point;
    return { point: constrained, label: fmtXY(constrained) };
}

// ============================================================================
// POINTER SYNTHESIS — deliver a resolved point to the active tool
// ============================================================================

/** PointerId used by all synthesized dynamic-input events (never collides
 *  with real mouse/touch ids which start at 1). */
export const DYN_POINTER_ID = 991;

/**
 * JS-dispatched PointerEvents report offsetX/offsetY = 0 (the browser only
 * derives them for real user input), but R3F's default event compute reads
 * exactly those fields. Shadow them with canvas-local CSS pixel offsets so
 * the synthetic raycast lands on the injected world point.
 */
function makeDynPointerEvent(
    type: string,
    rect: DOMRect,
    offsetX: number,
    offsetY: number,
    extra: PointerEventInit
): PointerEvent {
    const event = new PointerEvent(type, {
        bubbles: true,
        cancelable: true,
        composed: true,
        view: window,
        pointerId: DYN_POINTER_ID,
        pointerType: 'mouse',
        isPrimary: true,
        clientX: rect.left + offsetX,
        clientY: rect.top + offsetY,
        ...extra,
    });
    Object.defineProperty(event, 'offsetX', { value: offsetX });
    Object.defineProperty(event, 'offsetY', { value: offsetY });
    Object.defineProperty(event, 'layerX', { value: offsetX });
    Object.defineProperty(event, 'layerY', { value: offsetY });
    return event;
}

/**
 * Resolve a client (browser) coordinate into the world point on the ground
 * plane under it — the screen→world half of the drag-drop placement path
 * (Faz 5.8 Blocks palette drop → INSERT at the drop point).
 */
export function worldAtClient(clientX: number, clientY: number): THREE.Vector3 | null {
    const off = clientToOffset(clientX, clientY);
    return off ? intersectGroundAtOffset(off.ox, off.oy) : null;
}

/**
 * Inject a world point as a click for the active tool: the point is
 * projected to canvas pixels and replayed as pointermove + pointerdown +
 * pointerup, so R3F's own raycast pipeline updates previews and commits
 * the point exactly as a real click would — no per-tool API changes needed.
 */
export function injectPointAtWorld(world: THREE.Vector3): boolean {
    const { camera, canvas, invalidate } = engineRefs;
    if (!camera || !canvas) return false;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    const ndc = world.clone().project(camera);
    if (!Number.isFinite(ndc.x) || !Number.isFinite(ndc.y)) return false;
    const offsetX = (ndc.x * 0.5 + 0.5) * rect.width;
    const offsetY = (-ndc.y * 0.5 + 0.5) * rect.height;

    canvas.dispatchEvent(
        makeDynPointerEvent('pointermove', rect, offsetX, offsetY, { buttons: 0 })
    );
    canvas.dispatchEvent(
        makeDynPointerEvent('pointerdown', rect, offsetX, offsetY, { button: 0, buttons: 1 })
    );
    canvas.dispatchEvent(
        makeDynPointerEvent('pointerup', rect, offsetX, offsetY, { button: 0, buttons: 0 })
    );

    anchorPoint = world.clone();
    invalidate?.();
    return true;
}

// ============================================================================
// SHARED COMMAND EXECUTION (command line + cursor tooltip = one language)
// ============================================================================

export interface DraftCommandOutcome {
    success: boolean;
    message: string;
    /** Command-log lines, same shape the command panel / tooltip render. */
    lines: string[];
}

/**
 * Legacy parser actions that the CommandDispatcher hands over without a
 * tool activation (the preset layer normally claims these names first —
 * this map keeps the fallback honest when no preset resolves them).
 */
function legacyActionHandler(action?: string): ((lines: string[]) => void) | null {
    switch (action) {
        case 'erase':
            return () => runCanonicalCommand('mod.erase');
        case 'move': return (l) => { activateDraftTool('MOVE', l); };
        case 'copy': return (l) => { activateDraftTool('COPY', l); };
        case 'rotate': return (l) => { activateDraftTool('ROTATE', l); };
        case 'scale': return (l) => { activateDraftTool('SCALE', l); };
        case 'mirror': return (l) => { activateDraftTool('MIRROR_PICK', l); };
        case 'offset': return (l) => { activateDraftTool('OFFSET', l); };
        case 'fillet': return () => runCanonicalCommand('mod.fillet');
        case 'chamfer': return () => runCanonicalCommand('mod.chamfer');
        case 'layer': return () => { useAppStore.getState().toggleSidebar(); };
        case 'properties': return () => {
            const app = useAppStore.getState();
            if (!app.propertiesPanelOpen) app.togglePropertiesPanel();
        };
        default: return null;
    }
}

function activateDraftTool(tool: string, lines: string[]): void {
    useAppStore.getState().setActiveTool(tool as any);
    lines.push(`Tool activated: ${tool}`);
}

/**
 * Run one typed command through the shared dispatcher and apply the
 * parser-level side effects (activateTool / undo / redo / point). Single
 * source of truth for both the bottom command line and the cursor tooltip.
 */
export function executeDraftCommand(input: string): DraftCommandOutcome {
    const app = useAppStore.getState();
    const result = dispatchCommand(input);
    // Every command SOURCE lands in the shared recent-commands list (Faz
    // 4.7): typed lines, repeats and 'yap:run-command' replays all pass
    // through this gate, so the Repeat menu mirrors them all.
    if (result.success && input.trim()) {
        useDynamicInputStore.getState().pushRecentCommand(input.trim());
    }
    const lines: string[] = [`Command: ${input}`];
    if (result.success) {
        lines.push(result.message);
        const parsed = result.commandResult;
        if (parsed?.action === 'activateTool' && parsed.params?.tool) {
            const toolName = String(parsed.params.tool);
            // Navigation pseudo-tools from the legacy parser: report the
            // actual mouse model instead of claiming a non-existent tool.
            if (toolName === 'pan' || toolName === 'orbit' || toolName === 'zoom') {
                lines.push('Navigation: middle-drag pans · Alt+middle-drag orbits · wheel zooms.');
            } else {
                app.setActiveTool(toolName as any);
                lines.push(`Tool activated: ${toolName}`);
            }
        } else if (parsed?.action === 'undo') {
            app.undo();
        } else if (parsed?.action === 'undo.mark') {
            // Faz 10.3: UNDO Mark — the optional label names the rollback
            // point in the QAT history dropdown.
            app.markHistory(parsed.params?.label ? String(parsed.params.label) : undefined);
        } else if (parsed?.action === 'undo.back') {
            app.undoBack();
        } else if (parsed?.action === 'undo.begin') {
            app.beginHistoryGroup();
        } else if (parsed?.action === 'undo.end') {
            app.endHistoryGroup();
        } else if (parsed?.action === 'redo') {
            app.redo();
        } else if (parsed?.action === 'zoom') {
            // ZOOM is a 'system' command: the parser owns it and the shells
            // route the resulting camera operation through the shared zoom
            // events (ZoomChordHandler in every EngineViewport canvas).
            const mode = String(parsed.params?.mode ?? 'E');
            if (mode === 'E' || mode === 'A') {
                window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
            } else if (mode === 'P') {
                window.dispatchEvent(new CustomEvent('yap:zoom-previous'));
            } else if (mode === 'W') {
                // ZOOM Window: collect two ground corners through the shared
                // command-interact engine, then frame that region.
                const ci = useCommandInteract.getState();
                if (!ci.active) {
                    ci.start({
                        steps: [
                            { prompt: 'ZOOM Window: first corner', kind: 'point' },
                            { prompt: 'ZOOM Window: opposite corner', kind: 'point' },
                        ],
                        complete: (pts) => {
                            window.dispatchEvent(new CustomEvent('yap:zoom-region', {
                                detail: {
                                    a: [pts[0].x, pts[0].y, pts[0].z],
                                    b: [pts[1].x, pts[1].y, pts[1].z],
                                },
                            }));
                            return 'Zoom window applied.';
                        },
                    });
                    lines.push('Specify the first corner of the zoom window.');
                }
            } else if (mode === 'S') {
                // ZOOM Scale: numeric argument (2 = 2x, 0.5 = half).
                const factor = Number(parsed.params?.scale);
                if (Number.isFinite(factor) && factor > 0) {
                    window.dispatchEvent(new CustomEvent('yap:zoom-scale', { detail: { factor } }));
                    lines.push(`Zoom scale ${factor}x applied.`);
                } else {
                    lines.push('ZOOM Scale: usage — ZOOM S <factor> (e.g. ZOOM S 2).');
                }
            } else if (mode === 'O') {
                window.dispatchEvent(new CustomEvent('yap:zoom-to-selection'));
            } else {
                window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
            }
        } else if (parsed?.action === 'pan') {
            // PAN dx,dy: translate the plan view on screen (pixels → world).
            const dx = Number(parsed.params?.dx ?? 0);
            const dy = Number(parsed.params?.dy ?? 0);
            if (Number.isFinite(dx) && Number.isFinite(dy)) {
                window.dispatchEvent(new CustomEvent('yap:pan-by', { detail: { dx, dy } }));
                lines.push(`Pan by ${dx}, ${dy}.`);
            }
        } else if (parsed?.action && legacyActionHandler(parsed.action)) {
            legacyActionHandler(parsed.action)!(lines);
        } else if (parsed?.action === 'point' && parsed.params?.input !== undefined) {
            const outcome = commitParsedPoint(parsed.params);
            lines.push(outcome.line);
            lines.push('');
            return { success: outcome.ok, message: outcome.line, lines };
        }
    } else {
        lines.push(`Error: ${result.message}`);
    }
    lines.push('');
    return { success: result.success, message: result.message, lines };
}

/**
 * Deliver a parser `point` action (typed into the bottom command line) to
 * the active command through the same resolution path as the cursor
 * tooltip. Grammar: X,Y | @DX,DY | X,Y,Z | D<A | D. When the generic
 * CommandInteract engine is prompting, the point feeds its current step
 * directly; otherwise it is injected as a click into the active tool.
 */
export function commitParsedPoint(params: Record<string, unknown>): { ok: boolean; line: string } {
    const raw = String((params.input as string | undefined) ?? '');
    const interact = useCommandInteract.getState();

    if (interact.active && interact.spec) {
        const step = interact.spec.steps[interact.stepIndex];
        if (step?.kind !== 'point') return { ok: false, line: 'Error: current step does not take a point' };
        const m = raw.match(/^@?(-?\d*\.?\d+),(-?\d*\.?\d+)(?:,(-?\d*\.?\d+))?$/);
        if (m) {
            let x = parseFloat(m[1]);
            let y = parseFloat(m[2]);
            const z = m[3] !== undefined ? parseFloat(m[3]) : 0;
            if (raw.startsWith('@')) {
                if (!anchorPoint) return { ok: false, line: 'Error: no base point for relative input' };
                x += anchorPoint.x;
                y += anchorPoint.y;
            }
            const point = new THREE.Vector3(x, y, z);
            interact.addPoint(point);
            anchorPoint = point.clone();
            return { ok: true, line: `Point: ${x.toFixed(2)}, ${y.toFixed(2)}` };
        }
        const resolved = resolveDynamicPoint(raw);
        if (resolved && 'point' in resolved) {
            interact.addPoint(resolved.point);
            return { ok: true, line: `Point: ${resolved.label}` };
        }
        if (resolved && 'error' in resolved) return { ok: false, line: `Error: ${resolved.error}` };
        return { ok: false, line: 'Error: invalid point input' };
    }

    const tool = String(useAppStore.getState().activeTool ?? 'select').toLowerCase();
    if (tool === 'select') {
        return { ok: false, line: 'Error: no active command to receive the point' };
    }
    const m3 = raw.match(/^(-?\d*\.?\d+),(-?\d*\.?\d+),(-?\d*\.?\d+)$/);
    const resolved = m3
        ? { point: new THREE.Vector3(parseFloat(m3[1]), parseFloat(m3[2]), parseFloat(m3[3])), label: `x, y, z` }
        : resolveDynamicPoint(raw);
    if (!resolved) return { ok: false, line: 'Error: invalid point input' };
    if ('error' in resolved) return { ok: false, line: `Error: ${resolved.error}` };
    const ok = injectPointAtWorld(resolved.point);
    return ok
        ? { ok: true, line: `Point: ${fmtXY(resolved.point)}` }
        : { ok: false, line: 'Error: viewport not ready' };
}
